import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";

import { addAuthVars } from "@teamrota/authlib";

import useAuth from "~/src/auth/hooks/use-auth";
import LogFragment from "~/src/graphql/fragments/log/log.fragment";
import AccountRoleFragment from "~/src/graphql/fragments/account-role";

const GET_TARGET_ACCOUNT = addAuthVars(gql`
  ${LogFragment}
  ${AccountRoleFragment}
  query getTargetAccount($accountId: ID!) {
    account {
      id
      roles {
        ...AccountRoleFragment
      }

      connectionPolicy(targetAccountId: $accountId) {
        id
        minimumShiftLength
        minimumShiftPercentage
        cancellationPeriod
      }

      partnerConnection: connections(
        accountType: provider
        targetAccountId: $accountId
        limit: 1
        offset: 0
      ) {
        data {
          id
          isCandidatePromosEnabled
        }
      }

      targetAccount(accountId: $accountId) {
        id
        accountName
        logo
        createdAt
        accountName
        contactEmail
        contactWeb
        contactPhone
        isAutoAcceptEnabled
        isRequestAllEnabled
        isRebookingEnabled
        isSleepTimeEnabled
        notes
        ownerId
        subvenueSelection
        isSubvenueSelectionEnforced
        showVenuesInCandidatePreferences
        logs(targetAccountId: $accountId) {
          ...LogFragment
        }

        documents {
          id
          url
          format
          type
          label
        }

        s3Documents {
          id
          format
          type
          fileName
          url
        }

        matcherConfig {
          id
          hoursBeforeShift
          algoSkew
          favouriteFactor
          isPartnerManagedEnabled
          globalRatingFactor
          roleFactor
          relationshipFactor
          autoAcceptThreshold
        }

        users {
          id
          firstName
          lastName
          email
          phoneNumber
        }

        tags {
          id
          name
        }

        roleRates(accountType: requester) {
          id
          roleId
          startDate
          endDate
          role {
            name
          }

          ... on roleRate {
            payRate
            chargeRate
          }

          tags {
            id
            name
          }
        }
      }
    }
  }
`);

export const useTargetAccount = ({ accountId }) => {
  const auth = useAuth();

  const { data, loading, error, refetch } = useQuery(GET_TARGET_ACCOUNT, {
    skip: !accountId,
    variables: auth.addVals(GET_TARGET_ACCOUNT, { accountId }),
    fetchPolicy: "network-only",
    onError: error => {
      throw error;
    }
  });

  const targetAccount = data?.account?.targetAccount;

  return {
    targetAccount,
    connection: data?.account?.partnerConnection?.data?.[0],
    roles: data?.account?.roles || [],
    connectionPolicy: data?.account?.connectionPolicy,
    roleRates: targetAccount?.roleRates || [],
    loading,
    error,
    refetch
  };
};
